import React, { useState, useEffect } from 'react';
import { useAuth, apiClient } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    AreaChart, Area, LineChart, Line, Legend,
} from 'recharts';
import {
    Users, TrendingUp, Award, Target, DollarSign,
    Filter, ShieldCheck, Trophy, Zap, ChevronRight, Star,
    Eye, EyeOff,
} from 'lucide-react';
import { PerformanceInsightBanner } from '@/components/PerformanceInsightBanner';
import CallAnalyticsWidget from '@/components/CallAnalyticsWidget';
import AgentActivityTodayWidget from '@/components/AgentActivityTodayWidget';
import { Progress } from '@/components/ui/progress';
import logger from '@/lib/logger';

const COLORS = ['#EF3340', '#10b981', '#f59e0b', '#3b82f6', '#8b5cf6', '#06b6d4', '#ec4899', '#f97316'];
const PERIOD_OPTIONS = [
    { value: 'today', label: 'Today' }, { value: 'this_week', label: 'This Week' },
    { value: 'this_month', label: 'This Month' }, { value: 'this_quarter', label: 'This Quarter' },
    { value: 'this_year', label: 'This Year' }, { value: 'overall', label: 'Overall' },
    { value: 'custom', label: 'Custom Range' },
];

const fmtCur = (v) => new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED', minimumFractionDigits: 0 }).format(v || 0);

const ChartTooltip = ({ active, payload, label, formatter }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-popover border rounded-lg p-3 shadow-lg text-popover-foreground text-xs">
            <p className="font-medium mb-1">{label}</p>
            {payload.map((p, i) => (<p key={i} style={{ color: p.color }}>{p.name}: {formatter ? formatter(p.value) : p.value}</p>))}
        </div>
    );
};

const CSDashboard = () => {
    const { user } = useAuth();
    const [stats, setStats] = useState({});
    const [agentRevenue, setAgentRevenue] = useState([]);
    const [monthlyTrend, setMonthlyTrend] = useState([]);
    const [monthComparison, setMonthComparison] = useState({ data: [], this_month_label: '', last_month_label: '' });
    const [pipeline, setPipeline] = useState([]);
    const [leaderboard, setLeaderboard] = useState([]);
    const [agentBifurcation, setAgentBifurcation] = useState([]);
    const [myCommission, setMyCommission] = useState(null);
    const [netPayData, setNetPayData] = useState(null);
    const [csBonus, setCsBonus] = useState(null);
    const [agentBonusBreakdown, setAgentBonusBreakdown] = useState(null);
    const [period, setPeriod] = useState('overall');
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');
    const isHeadOrAdmin = ['cs_head', 'super_admin', 'admin'].includes(user?.role);
    const [viewMode, setViewMode] = useState(isHeadOrAdmin ? 'team' : 'individual');
    const [showSensitive, setShowSensitive] = useState(false);
    const MASKED = '••••••';
    const [loading, setLoading] = useState(true);
    const [drill, setDrill] = useState({ open: false, title: '', content: null, loading: false });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchAll(); }, [period, viewMode, customStart, customEnd]);

    const periodQuery = () => {
        let q = `period=${period}`;
        if (period === 'custom' && customStart) q += `&custom_start=${customStart}`;
        if (period === 'custom' && customEnd) q += `&custom_end=${customEnd}`;
        return q;
    };

    const fetchAll = async () => {
        try {
            setLoading(true);
            const myViewMode = isHeadOrAdmin ? viewMode : 'individual';
            const pq = periodQuery();
            // Use batch endpoint for core data + parallel remaining calls
            const [batchRes, ...extraResults] = await Promise.allSettled([
                apiClient.get(`/cs/dashboard/batch?${pq}&view_mode=${myViewMode}`),
                apiClient.get(`/cs/dashboard/agent-revenue?${pq}`),
                isHeadOrAdmin ? apiClient.get(`/dashboard/cs-agent-bifurcation?${pq}`) : Promise.resolve({ data: [] }),
                apiClient.get('/commissions/dashboard'),
                apiClient.get('/commissions/scatter-data'),
                isHeadOrAdmin ? apiClient.get('/cs/dashboard/agent-bonus-breakdown') : Promise.resolve({ data: null }),
            ]);
            const batch = batchRes.status === 'fulfilled' ? batchRes.value.data : {};
            const extra = (i, fb = {}) => extraResults[i]?.status === 'fulfilled' ? extraResults[i].value.data : fb;
            setStats(batch.stats || {});
            setMonthlyTrend(batch.monthly_trend || []);
            setMonthComparison(batch.month_comparison || { data: [], this_month_label: '', last_month_label: '' });
            setPipeline(batch.pipeline || []);
            setLeaderboard(batch.leaderboard || []);
            setCsBonus(batch.bonus || null);
            setAgentRevenue(extra(0, []) || []);
            setAgentBifurcation(extra(1, []) || []);
            setMyCommission(extra(2, null));
            setNetPayData(extra(3, null));
            setAgentBonusBreakdown(extra(4, null));
        } catch (error) { logger.error('Failed to fetch CS dashboard:', error); }
        finally { setLoading(false); }
    };

    const canDrillDown = true;
    const closeDrill = () => setDrill({ open: false, title: '', content: null, loading: false });
    const openDrillLoading = (title) => setDrill({ open: true, title, content: null, loading: true });

    // ====== CS Agent Students drill ======
    const drillCsAgent = async (agentName) => {
        openDrillLoading(`${agentName} — Student Details`);
        try {
            const res = await apiClient.get(`/cs/drill/agent-students?agent_name=${encodeURIComponent(agentName)}&period=${period}`);
            const data = res.data || {};
            const students = data.students || (Array.isArray(data) ? data : []);
            const withUpgrades = students.filter(s => s.upgrade_count > 0);
            const totalRev = data.total_revenue ?? students.reduce((s, st) => s + (st.upgrade_revenue || 0), 0);
            const totalUps = data.total_upgrades ?? withUpgrades.reduce((s, st) => s + st.upgrade_count, 0);
            const periodLabel = PERIOD_OPTIONS.find(o => o.value === period)?.label || period;
            setDrill(prev => ({
                ...prev, loading: false,
                content: (
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 flex-wrap">
                            <Badge variant="secondary">{students.length} students</Badge>
                            <Badge variant="outline" className="text-emerald-500 border-emerald-500/30">{totalUps} upgrades</Badge>
                            <Badge variant="outline" className="text-amber-500 border-amber-500/30">{fmtCur(totalRev)} revenue</Badge>
                            <Badge variant="default" className="text-xs">{periodLabel}</Badge>
                        </div>
                        <Table>
                            <TableHeader><TableRow><TableHead>Student</TableHead><TableHead>Code</TableHead><TableHead>Stage</TableHead><TableHead>Course</TableHead><TableHead className="text-right">Upgrades</TableHead><TableHead className="text-right">Revenue</TableHead></TableRow></TableHeader>
                            <TableBody>
                                {students.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No upgrades in this period</TableCell></TableRow>}
                                {students.map((s, i) => (
                                    <TableRow key={i}>
                                        <TableCell className="font-medium">{s.student_name}</TableCell>
                                        <TableCell className="text-muted-foreground font-mono text-xs">{s.student_code || '-'}</TableCell>
                                        <TableCell><Badge variant="outline" className="text-xs capitalize">{(s.stage || '').replace(/_/g, ' ')}</Badge></TableCell>
                                        <TableCell className="text-muted-foreground">{s.course}</TableCell>
                                        <TableCell className="text-right">{s.upgrade_count}</TableCell>
                                        <TableCell className="text-right font-mono text-emerald-500">{s.upgrade_revenue > 0 ? fmtCur(s.upgrade_revenue) : '-'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )
            }));
        } catch { setDrill(prev => ({ ...prev, loading: false, content: <p className="text-center text-muted-foreground py-4">Failed to load</p> })); }
    };

    // ====== CS Pipeline drill ======
    const drillCsPipeline = async (stageLabel) => {
        openDrillLoading(`${stageLabel} — Student Details`);
        try {
            // CS pipeline always shows pitched_for_upgrade students, filter by upgrade path label
            const res = await apiClient.get(`/cs/drill/pipeline-stage?stage=pitched_for_upgrade&upgrade_path=${encodeURIComponent(stageLabel)}`);
            const d = res.data || {};
            setDrill(prev => ({
                ...prev, loading: false,
                content: (
                    <div className="space-y-4">
                        <Badge variant="outline" className="text-lg px-3 py-1">{d.total} students</Badge>
                        <div>
                            <h4 className="text-sm font-semibold mb-2">Agent-wise Breakdown</h4>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {(d.agent_breakdown || []).map((a, i) => (
                                    <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted/70"
                                        onClick={() => drillCsAgent(a.agent_name)}>
                                        <span className="text-sm font-medium truncate">{a.agent_name}</span>
                                        <div className="flex items-center gap-1"><Badge variant="secondary">{a.count}</Badge><ChevronRight className="h-3 w-3 text-muted-foreground" /></div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div>
                            <h4 className="text-sm font-semibold mb-2">Students</h4>
                            <Table>
                                <TableHeader><TableRow><TableHead>Student</TableHead><TableHead>Code</TableHead><TableHead>Agent</TableHead><TableHead>Course</TableHead><TableHead>Pitched</TableHead></TableRow></TableHeader>
                                <TableBody>{(d.students || []).slice(0, 50).map((s, i) => (
                                    <TableRow key={i}>
                                        <TableCell className="font-medium">{s.student_name}</TableCell>
                                        <TableCell className="font-mono text-xs text-muted-foreground">{s.student_code || '-'}</TableCell>
                                        <TableCell className="text-muted-foreground">{s.agent || '-'}</TableCell>
                                        <TableCell className="text-muted-foreground">{s.course}</TableCell>
                                        <TableCell className="text-xs">{s.pitched || '-'}</TableCell>
                                    </TableRow>
                                ))}</TableBody>
                            </Table>
                        </div>
                    </div>
                )
            }));
        } catch { setDrill(prev => ({ ...prev, loading: false, content: <p className="text-center text-muted-foreground py-4">Failed to load</p> })); }
    };

    // ====== CS Bifurcation drill ======
    const drillBifurcation = async (agent) => {
        openDrillLoading(`${agent.agent_name} — Stage Breakdown`);
        try {
            const res = await apiClient.get(`/cs/drill/agent-students?agent_name=${encodeURIComponent(agent.agent_name)}`);
            const students = res.data || [];
            const stageGroups = {};
            students.forEach(s => { const st = s.stage || 'unknown'; if (!stageGroups[st]) stageGroups[st] = []; stageGroups[st].push(s); });
            setDrill(prev => ({
                ...prev, loading: false,
                content: (
                    <div className="space-y-4">
                        <div className="flex items-center gap-3">
                            <Badge variant="secondary">{students.length} total students</Badge>
                            <Badge variant="outline">SLA Rate: {agent.sla_rate || 0}%</Badge>
                        </div>
                        <div>
                            <h4 className="text-sm font-semibold mb-2">By Stage</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                                {Object.entries(stageGroups).map(([stage, sts]) => (
                                    <div key={stage} className="p-3 rounded-lg bg-muted/50 text-center">
                                        <p className="text-lg font-bold">{sts.length}</p>
                                        <p className="text-xs text-muted-foreground capitalize">{stage.replace(/_/g, ' ')}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <Table>
                            <TableHeader><TableRow><TableHead>Student</TableHead><TableHead>Stage</TableHead><TableHead>Course</TableHead><TableHead className="text-right">Upgrades</TableHead></TableRow></TableHeader>
                            <TableBody>{students.slice(0, 50).map((s, i) => (
                                <TableRow key={i}>
                                    <TableCell className="font-medium">{s.student_name}</TableCell>
                                    <TableCell><Badge variant="outline" className="text-xs capitalize">{(s.stage || '').replace(/_/g, ' ')}</Badge></TableCell>
                                    <TableCell className="text-muted-foreground">{s.course}</TableCell>
                                    <TableCell className="text-right">{s.upgrade_count || 0}</TableCell>
                                </TableRow>
                            ))}</TableBody>
                        </Table>
                    </div>
                )
            }));
        } catch { setDrill(prev => ({ ...prev, loading: false, content: <p className="text-center text-muted-foreground py-4">Failed to load</p> })); }
    };

    if (loading) return (<div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div></div>);

    const totalPipeline = pipeline.reduce((s, p) => s + p.revenue, 0);

    return (
        <div className="space-y-6" data-testid="cs-dashboard">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">CS Dashboard</h1>
                    <p className="text-muted-foreground">Upgrade revenue, commissions & team performance — click charts to drill down</p>
                    <div className="mt-1"><CallAnalyticsWidget compact /></div>
                </div>
                <div className="flex items-center gap-3">
                    {isHeadOrAdmin && (
                        <div className="inline-flex rounded-lg border p-1 bg-muted/30" data-testid="cs-dash-view-toggle">
                            <button onClick={() => setViewMode('individual')} className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${viewMode === 'individual' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>Individual</button>
                            <button onClick={() => setViewMode('team')} className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${viewMode === 'team' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>Team</button>
                        </div>
                    )}
                    <Select value={period} onValueChange={setPeriod}>
                        <SelectTrigger className="w-[150px]" data-testid="cs-period-filter"><Filter className="h-3 w-3 mr-1" /><SelectValue /></SelectTrigger>
                        <SelectContent>{PERIOD_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <button onClick={() => setShowSensitive(!showSensitive)} className="p-2 rounded-lg border bg-muted/30 hover:bg-muted/70 transition-colors text-muted-foreground hover:text-foreground" data-testid="cs-toggle-sensitive-btn" aria-label={showSensitive ? 'Hide sensitive data' : 'Show sensitive data'} title={showSensitive ? 'Hide commissions' : 'Show commissions'}>
                        {showSensitive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </button>
                    {period === 'custom' && (
                        <div className="flex items-center gap-2">
                            <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                                className="h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="cs-custom-start" />
                            <span className="text-xs text-muted-foreground">to</span>
                            <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                                className="h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="cs-custom-end" />
                        </div>
                    )}
                </div>
            </div>

            <PerformanceInsightBanner endpoint="/cs/dashboard/performance-insight" />

            <AgentActivityTodayWidget scope="cs" />

            {/* Key Metrics */}
            <div className={`grid grid-cols-2 ${isHeadOrAdmin ? 'lg:grid-cols-5' : 'lg:grid-cols-3'} gap-4`}>
                <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border-emerald-500/20">
                    <CardContent className="pt-5 pb-4"><div className="flex items-center justify-between"><div><p className="text-xs text-muted-foreground uppercase tracking-wider">Achieved Revenue</p><p className="text-2xl font-bold font-mono text-emerald-500 mt-1" data-testid="achieved-revenue">{fmtCur(stats.achieved_revenue)}</p><p className="text-xs text-muted-foreground mt-1">{stats.achieved_count || 0} upgrades</p></div><div className="p-2.5 rounded-xl bg-emerald-500/20"><DollarSign className="h-6 w-6 text-emerald-500" /></div></div></CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 border-amber-500/20">
                    <CardContent className="pt-5 pb-4"><div className="flex items-center justify-between"><div><p className="text-xs text-muted-foreground uppercase tracking-wider">Pipeline Revenue</p><p className="text-2xl font-bold font-mono text-amber-500 mt-1" data-testid="pipeline-revenue">{fmtCur(stats.pipeline_revenue)}</p><p className="text-xs text-muted-foreground mt-1">{stats.pipeline_count || 0} pitched</p></div><div className="p-2.5 rounded-xl bg-amber-500/20"><Target className="h-6 w-6 text-amber-500" /></div></div></CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/20">
                    <CardContent className="pt-5 pb-4"><div className="flex items-center justify-between"><div><p className="text-xs text-muted-foreground uppercase tracking-wider">Agent Commission</p><p className="text-2xl font-bold font-mono text-blue-500 mt-1" data-testid="agent-commission">{showSensitive ? fmtCur(stats.total_agent_commission) : MASKED}</p></div><div className="p-2.5 rounded-xl bg-blue-500/20"><Award className="h-6 w-6 text-blue-500" /></div></div></CardContent>
                </Card>
                {isHeadOrAdmin && (
                    <Card className="bg-gradient-to-br from-purple-500/10 to-purple-500/5 border-purple-500/20">
                        <CardContent className="pt-5 pb-4"><div className="flex items-center justify-between"><div><p className="text-xs text-muted-foreground uppercase tracking-wider">Head Commission</p><p className="text-2xl font-bold font-mono text-purple-500 mt-1" data-testid="head-commission">{showSensitive ? fmtCur(stats.total_head_commission) : MASKED}</p></div><div className="p-2.5 rounded-xl bg-purple-500/20"><ShieldCheck className="h-6 w-6 text-purple-500" /></div></div></CardContent>
                    </Card>
                )}
                {isHeadOrAdmin && (
                    <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                        <CardContent className="pt-5 pb-4"><div className="flex items-center justify-between"><div><p className="text-xs text-muted-foreground uppercase tracking-wider">Total Commission</p><p className="text-2xl font-bold font-mono text-primary mt-1" data-testid="total-commission">{showSensitive ? fmtCur(stats.total_commission) : MASKED}</p></div><div className="p-2.5 rounded-xl bg-primary/20"><Zap className="h-6 w-6 text-primary" /></div></div></CardContent>
                    </Card>
                )}
            </div>

            {/* My Commission & Net Pay Summary */}
            {myCommission && myCommission.my_commission && (() => {
                const mc = myCommission.my_commission;
                const isApproved = myCommission.approval_status === 'approved';
                const approvedComm = myCommission.approved_commission || 0;
                const pendingApprovalComm = myCommission.pending_approval_commission || 0;
                const headEarned = isApproved ? (myCommission.approved_cs_head_commission || 0) : 0;
                const headPendingApproval = !isApproved ? (myCommission.pending_approval_cs_head_commission || myCommission.total_cs_head_earned || 0) : 0;
                const totalApproved = approvedComm + (isHeadOrAdmin ? headEarned : 0);
                const totalPendingApproval = pendingApprovalComm + (isHeadOrAdmin ? headPendingApproval : 0);
                const totalPipeline = (mc.pending_commission || 0) + (isHeadOrAdmin ? (myCommission.total_cs_head_pending || 0) : 0);
                const salary = netPayData?.base_salary || 0;
                const netPay = salary + totalApproved;
                return (
                    <Card data-testid="cs-my-commission">
                        <CardHeader className="pb-3">
                                <CardTitle className="text-base flex items-center gap-2"><DollarSign className="h-5 w-5 text-emerald-500" />My Commission — {myCommission.month}</CardTitle>
                            <CardDescription>
                                {mc.upgrades_closed || 0} personal upgrades closed this month
                                {isApproved ? (
                                    <Badge className="ml-2 bg-emerald-500/10 text-emerald-600 text-xs">CEO Approved</Badge>
                                ) : (
                                    <Badge className="ml-2 bg-amber-500/10 text-amber-600 text-xs">Pending CEO Approval</Badge>
                                )}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Net Pay + Commission Summary */}
                            <div className={`grid ${isHeadOrAdmin ? 'grid-cols-2 md:grid-cols-5' : 'grid-cols-2 md:grid-cols-3'} gap-3`}>
                                <div className={`p-3 rounded-lg border ${isApproved ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-amber-500/10 border-amber-500/20'}`}>
                                    <p className="text-xs text-muted-foreground">{isApproved ? 'Agent Commission (Approved)' : 'Agent Commission (Pending Approval)'}</p>
                                    <p className={`text-xl font-bold font-mono ${isApproved ? 'text-emerald-500' : 'text-amber-500'}`} data-testid="cs-my-agent-commission">{showSensitive ? fmtCur(isApproved ? approvedComm : pendingApprovalComm) : MASKED}</p>
                                    <p className="text-xs text-muted-foreground">{mc.upgrades_closed || 0} upgrades</p>
                                </div>
                                {isHeadOrAdmin && (
                                    <div className={`p-3 rounded-lg border ${isApproved ? 'bg-purple-500/10 border-purple-500/20' : 'bg-purple-400/10 border-purple-400/20'}`}>
                                        <p className="text-xs text-muted-foreground">{isApproved ? 'Head Commission (Approved)' : 'Head Commission (Pending)'}</p>
                                        <p className={`text-xl font-bold font-mono ${isApproved ? 'text-purple-500' : 'text-purple-400'}`} data-testid="cs-my-head-commission">{showSensitive ? fmtCur(isApproved ? headEarned : headPendingApproval) : MASKED}</p>
                                        <p className="text-xs text-muted-foreground">From team upgrades</p>
                                    </div>
                                )}
                                <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                                    <p className="text-xs text-muted-foreground">{isApproved ? 'Total Approved' : 'Total (Pending Approval)'}</p>
                                    <p className="text-xl font-bold font-mono text-blue-500" data-testid="cs-my-total-commission">{showSensitive ? fmtCur(isApproved ? totalApproved : totalPendingApproval) : MASKED}</p>
                                    {totalPipeline > 0 && <p className="text-xs text-orange-500">+{showSensitive ? fmtCur(totalPipeline) : MASKED} pipeline</p>}
                                </div>
                                <div className="p-3 rounded-lg bg-muted/50 border">
                                    <p className="text-xs text-muted-foreground">Base Salary</p>
                                    <p className="text-xl font-bold font-mono" data-testid="cs-my-salary">{showSensitive ? fmtCur(salary) : MASKED}</p>
                                    <p className="text-xs text-muted-foreground">Monthly</p>
                                </div>
                                <div className="p-3 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20">
                                    <p className="text-xs text-muted-foreground">Net Pay{!isApproved ? ' (Estimated)' : ''}</p>
                                    <p className="text-xl font-bold font-mono text-primary" data-testid="cs-my-net-pay">{showSensitive ? fmtCur(isApproved ? netPay : salary + totalPendingApproval) : MASKED}</p>
                                    <p className="text-xs text-muted-foreground">Salary + Commission</p>
                                </div>
                            </div>

                            {/* Transaction Details */}
                            {(mc.earned_details?.length > 0 || mc.pending_details?.length > 0) && (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Student</TableHead>
                                            <TableHead>Upgrade</TableHead>
                                            <TableHead>Stage</TableHead>
                                            <TableHead className="text-right">Amount</TableHead>
                                            <TableHead className="text-right">Commission</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {[...(mc.earned_details || []), ...(mc.pending_details || [])].map((d, i) => (
                                            <TableRow key={i}>
                                                <TableCell className="font-medium text-sm">{d.student_name}</TableCell>
                                                <TableCell className="text-xs text-muted-foreground">{d.upgrade_name || d.course_matched || '-'}</TableCell>
                                                <TableCell>
                                                    <Badge variant="outline" className={`text-xs ${d.status === 'earned' || d.stage === 'closed' ? 'text-emerald-500 border-emerald-500/30' : 'text-amber-500 border-amber-500/30'}`}>
                                                        {d.status || d.stage || 'pending'}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-right font-mono text-sm">{fmtCur(d.amount || d.upgrade_amount)}</TableCell>
                                                <TableCell className="text-right font-mono font-semibold text-sm">
                                                    <span className={d.status === 'earned' || d.stage === 'closed' ? 'text-emerald-500' : 'text-amber-500'}>
                                                        {fmtCur(d.cs_commission || d.agent_commission || d.se_commission || 0)}
                                                    </span>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                );
            })()}

            {/* Agent Revenue + Leaderboard */}
            {/* CS Bonus Progress — Per-Agent Breakdown for Admin, Personal for Agents */}
            {isHeadOrAdmin && agentBonusBreakdown?.agents?.length > 0 && (
                <Card data-testid="cs-agent-bonus-breakdown">
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle className="flex items-center gap-2 text-base">
                                    <Star className="h-5 w-5 text-amber-500" />
                                    Agent Bonus Tracker — {agentBonusBreakdown.month}
                                </CardTitle>
                                <CardDescription>Individual bonus progress per CS agent this month (AED 40k–100k target)</CardDescription>
                            </div>
                            <Badge variant="outline" className="text-amber-500 border-amber-500/40 font-mono text-sm">
                                Total: {fmtCur(agentBonusBreakdown.total_bonus)}
                            </Badge>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <ResponsiveContainer width="100%" height={Math.max(200, agentBonusBreakdown.agents.length * 52)}>
                            <BarChart data={agentBonusBreakdown.agents} layout="vertical" margin={{ left: 10, right: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-border" horizontal={false} />
                                <XAxis type="number" tick={{ className: 'fill-muted-foreground', fontSize: 11 }} tickFormatter={v => `${(v / 1000).toFixed(0)}K`} />
                                <YAxis dataKey="agent_name" type="category" tick={{ className: 'fill-muted-foreground', fontSize: 11 }} width={120} />
                                <Tooltip content={({ active, payload, label }) => {
                                    if (!active || !payload?.length) return null;
                                    const d = payload[0]?.payload;
                                    const flat = agentBonusBreakdown.use_flat_tiers;
                                    return (
                                        <div className="bg-popover border rounded-lg p-3 shadow-lg text-popover-foreground text-xs space-y-1">
                                            <p className="font-semibold">{label}</p>
                                            <p>Revenue: {fmtCur(d?.revenue)}</p>
                                            <p>Upgrades: {d?.upgrades}</p>
                                            <p>Tier: {flat ? `AED ${d?.bonus_amount} flat` : `${d?.bonus_pct}%`}</p>
                                            <p className="font-bold text-amber-500">Bonus: {fmtCur(d?.bonus_amount)}</p>
                                        </div>
                                    );
                                }} />
                                <Bar dataKey="revenue" fill="#f59e0b" radius={[0, 4, 4, 0]} name="Revenue" />
                            </BarChart>
                        </ResponsiveContainer>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                            {agentBonusBreakdown.agents.map((a) => (
                                <div key={a.agent_id} className={`p-3 rounded-lg border ${a.bonus_amount > 0 ? 'bg-amber-500/10 border-amber-500/20' : 'bg-muted/30 border-border'}`}>
                                    <p className="text-xs font-medium truncate">{a.agent_name}</p>
                                    <p className="text-lg font-bold font-mono mt-1" data-testid={`cs-bonus-${a.agent_name.replace(/\s+/g, '-').toLowerCase()}`}>
                                        {a.bonus_amount > 0 ? <span className="text-amber-500">{fmtCur(a.bonus_amount)}</span> : <span className="text-muted-foreground">AED 0</span>}
                                    </p>
                                    <p className="text-[10px] text-muted-foreground mt-0.5">{fmtCur(a.revenue)} rev · {agentBonusBreakdown.use_flat_tiers ? `AED ${a.bonus_amount || 0} tier` : `${a.bonus_pct}% slab`}</p>
                                    <Progress value={Math.min(100, (a.revenue / 100000) * 100)} className="h-1.5 mt-1.5 [&>div]:bg-amber-500" />
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}
            {!isHeadOrAdmin && csBonus && csBonus.slabs && (
                <Card data-testid="cs-bonus-progress">
                    <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-base">
                            <Star className="h-5 w-5 text-amber-500" />
                            My Bonus Progress
                        </CardTitle>
                        <CardDescription>Upgrade revenue this month: {fmtCur(csBonus.month_revenue || 0)} — Target: AED 40k - 100k</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="space-y-1.5">
                            <div className="flex justify-between text-xs">
                                <span>{csBonus.current_slab ? (csBonus.use_flat_tiers ? `AED ${csBonus.current_slab.bonus_amount} tier achieved` : `${csBonus.current_slab.bonus_pct}% slab achieved`) : (csBonus.month_revenue < 40000 ? `${fmtCur(40000 - csBonus.month_revenue)} to unlock bonus` : 'No slab reached')}</span>
                                {csBonus.next_slab && <span className="text-muted-foreground">{fmtCur(csBonus.next_slab.threshold - (csBonus.month_revenue || 0))} to {csBonus.use_flat_tiers ? `AED ${csBonus.next_slab.bonus_amount}` : `${csBonus.next_slab.bonus_pct}%`}</span>}
                            </div>
                            <Progress value={csBonus.next_slab ? Math.min(100, ((csBonus.month_revenue || 0) / csBonus.next_slab.threshold) * 100) : (csBonus.current_slab ? 100 : 0)} className="h-2.5 [&>div]:bg-amber-500" />
                        </div>
                        {csBonus.current_slab && (
                            <div className="bg-amber-500/10 rounded-lg p-3 flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium">Current Bonus</p>
                                    <p className="text-xs text-muted-foreground">{csBonus.use_flat_tiers ? `Flat tier — AED ${(csBonus.current_slab.threshold/1000).toFixed(0)}k+ revenue` : `${csBonus.current_slab.bonus_pct}% of salary (${fmtCur(csBonus.salary_aed)})`}</p>
                                </div>
                                <p className="text-xl font-bold text-amber-500 font-mono">{fmtCur(csBonus.bonus_amount_aed || 0)}</p>
                            </div>
                        )}
                        {!csBonus.current_slab && csBonus.month_revenue < 40000 && (
                            <div className="bg-muted/50 rounded-lg p-3 text-center">
                                <p className="text-sm text-muted-foreground">No bonus below AED 40,000 — keep pushing!</p>
                            </div>
                        )}
                        <div className={`grid gap-1 text-center ${csBonus.slabs.length > 4 ? 'grid-cols-7' : 'grid-cols-4'}`}>
                            {csBonus.slabs.map((s) => (
                                <div key={s.threshold} className={`p-1.5 rounded text-[10px] ${(csBonus.month_revenue || 0) >= s.threshold ? 'bg-amber-500/20 text-amber-500' : 'bg-muted/50 text-muted-foreground'}`}>
                                    <p className="font-bold">{csBonus.use_flat_tiers ? `AED ${s.bonus_amount}` : `${s.bonus_pct}%`}</p>
                                    <p>{(s.threshold/1000).toFixed(0)}k</p>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Agent Revenue + Leaderboard */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="lg:col-span-2">
                    <CardHeader className="pb-2"><CardTitle className="text-base">Agent Revenue Closed</CardTitle><CardDescription>Click any bar to see agent's students</CardDescription></CardHeader>
                    <CardContent>
                        {agentRevenue.length > 0 ? (
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={agentRevenue} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                                    <XAxis type="number" tick={{ className: 'fill-muted-foreground', fontSize: 11 }} tickFormatter={v => `${(v / 1000).toFixed(0)}K`} />
                                    <YAxis dataKey="agent_name" type="category" tick={{ className: 'fill-muted-foreground', fontSize: 11 }} width={100} />
                                    <Tooltip content={<ChartTooltip formatter={fmtCur} />} />
                                    <Bar dataKey="revenue" fill="#10b981" radius={[0, 4, 4, 0]} name="Revenue" cursor={canDrillDown ? "pointer" : "default"}
                                        onClick={(data) => { if (canDrillDown && data?.agent_name) drillCsAgent(data.agent_name); }} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (<div className="h-64 flex items-center justify-center text-muted-foreground">No revenue data yet</div>)}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-500" /> Leaderboard</CardTitle><CardDescription>Click agent to see their students & upgrades</CardDescription></CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {leaderboard.slice(0, 5).map((agent, idx) => (
                                <div key={idx} className="flex items-center gap-3 p-2 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                                    onClick={() => drillCsAgent(agent.agent_name)} data-testid={`cs-leaderboard-${idx}`}>
                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${idx === 0 ? 'bg-amber-500 text-white' : idx === 1 ? 'bg-gray-400 text-white' : idx === 2 ? 'bg-amber-700 text-white' : 'bg-muted text-muted-foreground'}`}>{idx + 1}</div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate">{agent.agent_name}</p>
                                        <p className="text-xs text-muted-foreground">{agent.upgrades} upgrades</p>
                                    </div>
                                    <div className="text-right flex items-center gap-1">
                                        <div><p className="text-sm font-bold font-mono text-emerald-500">{fmtCur(agent.revenue)}</p><p className="text-[10px] text-muted-foreground">{agent.upgrades} upgrades</p></div>
                                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                    </div>
                                </div>
                            ))}
                            {leaderboard.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No upgrade data yet</p>}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Monthly Trend + Month vs Month */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base">Monthly Revenue Trend</CardTitle><CardDescription>Upgrade revenue & commission over time</CardDescription></CardHeader>
                    <CardContent>
                        {monthlyTrend.length > 0 ? (
                            <ResponsiveContainer width="100%" height={260}>
                                <AreaChart data={monthlyTrend}>
                                    <defs><linearGradient id="csRevGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.3} /><stop offset="95%" stopColor="#10b981" stopOpacity={0} /></linearGradient></defs>
                                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                                    <XAxis dataKey="month" tick={{ className: 'fill-muted-foreground', fontSize: 10 }} />
                                    <YAxis tick={{ className: 'fill-muted-foreground', fontSize: 11 }} tickFormatter={v => `${(v / 1000).toFixed(0)}K`} />
                                    <Tooltip content={<ChartTooltip formatter={fmtCur} />} />
                                    <Area type="monotone" dataKey="revenue" stroke="#10b981" fill="url(#csRevGrad)" strokeWidth={2} name="Revenue" />
                                </AreaChart>
                            </ResponsiveContainer>
                        ) : (<div className="h-52 flex items-center justify-center text-muted-foreground">No monthly data</div>)}
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base">This Month vs Last Month</CardTitle><CardDescription>{monthComparison.this_month_label} vs {monthComparison.last_month_label} (cumulative)</CardDescription></CardHeader>
                    <CardContent>
                        {monthComparison.data.length > 0 ? (
                            <ResponsiveContainer width="100%" height={260}>
                                <LineChart data={monthComparison.data}>
                                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                                    <XAxis dataKey="day" tick={{ className: 'fill-muted-foreground', fontSize: 11 }} />
                                    <YAxis tick={{ className: 'fill-muted-foreground', fontSize: 11 }} tickFormatter={v => `${(v / 1000).toFixed(0)}K`} />
                                    <Tooltip content={<ChartTooltip formatter={fmtCur} />} />
                                    <Line type="monotone" dataKey="this_month" stroke="#10b981" strokeWidth={2.5} dot={false} name={monthComparison.this_month_label} />
                                    <Line type="monotone" dataKey="last_month" stroke="#6b7280" strokeWidth={2} strokeDasharray="5 5" dot={false} name={monthComparison.last_month_label} />
                                    <Legend />
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (<div className="h-52 flex items-center justify-center text-muted-foreground">No comparison data</div>)}
                    </CardContent>
                </Card>
            </div>

            {/* Pipeline + Agent Bifurcation */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                            <div><CardTitle className="text-base">Upgrade Pipeline</CardTitle><CardDescription>Click any item to see student details</CardDescription></div>
                            <Badge variant="outline" className="text-amber-500 border-amber-500/40 font-mono">{fmtCur(totalPipeline)}</Badge>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {pipeline.length > 0 ? (
                            <div className="space-y-3">
                                {pipeline.map((p, idx) => (
                                    <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                                        onClick={() => drillCsPipeline(p.label)}>
                                        <div className="p-2 rounded-lg" style={{ backgroundColor: `${COLORS[idx % COLORS.length]}20` }}>
                                            <Target className="h-4 w-4" style={{ color: COLORS[idx % COLORS.length] }} />
                                        </div>
                                        <div className="flex-1"><p className="text-sm font-medium">{p.label}</p><p className="text-xs text-muted-foreground">{p.count} students</p></div>
                                        <div className="flex items-center gap-2"><p className="text-lg font-bold font-mono">{fmtCur(p.revenue)}</p><ChevronRight className="h-4 w-4 text-muted-foreground" /></div>
                                    </div>
                                ))}
                            </div>
                        ) : (<div className="h-40 flex flex-col items-center justify-center text-muted-foreground"><Target className="h-8 w-8 mb-2 opacity-30" /><p className="text-sm">No upgrades in pipeline</p></div>)}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> Agent Bifurcation</CardTitle><CardDescription>Click any bar to see agent's student breakdown by stage</CardDescription></CardHeader>
                    <CardContent>
                        {agentBifurcation.length > 0 ? (
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={agentBifurcation}>
                                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                                    <XAxis dataKey="agent_name" tick={{ className: 'fill-muted-foreground', fontSize: 9 }} angle={-15} textAnchor="end" height={50} />
                                    <YAxis tick={{ className: 'fill-muted-foreground', fontSize: 11 }} />
                                    <Tooltip content={<ChartTooltip />} />
                                    <Bar dataKey="total_students" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Total" cursor="pointer"
                                        onClick={(data) => { if (data?.agent_name) drillBifurcation(data); }} />
                                    <Bar dataKey="activated" fill="#10b981" radius={[4, 4, 0, 0]} name="Activated" cursor="pointer"
                                        onClick={(data) => { if (data?.agent_name) drillBifurcation(data); }} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (<div className="h-52 flex items-center justify-center text-muted-foreground">No agent data</div>)}
                    </CardContent>
                </Card>
            </div>

            {/* Drill-Down Dialog */}
            <Dialog open={drill.open} onOpenChange={(o) => { if (!o) closeDrill(); }}>
                <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="cs-drill-modal">
                    <DialogHeader><DialogTitle>{drill.title}</DialogTitle><DialogDescription>Detailed view</DialogDescription></DialogHeader>
                    <div className="overflow-y-auto flex-1">
                        {drill.loading && <div className="flex items-center justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div></div>}
                        {!drill.loading && drill.content}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CSDashboard;
